import React, {useState, useEffect, useRef, useContext} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Button from '../Button';
import Icon from '../Icon';
import Tooltip from 'bootstrap/js/dist/tooltip';
import withAppContext from "../../hooks/withAppContext";
import Loading from "../Loading/index";
import RawHTML from "../RawHTML";
export interface IVHSSignupRadios {
	/**
	 * Price Dollars
	 */
	priceDollar?: string;
	/**
	 * Price Cent
	 */
	priceCent?: string;
	/**
	 * Fast Track Price Dollars
	 */
	priceDollarFast?: string;
	/**
	 * Fast Track Price Cent
	 */
	priceCentFast?: string;
	/**
	 * Icon
	 */
	priceIcon?:  'canada' | 'international';
	/**
	 *  Item Label
	 */
	name?: string;
}

export interface IVHSSignupItems {
	/**
	 * Icon
	 */
	icon?:  'bell' | 'calendar' | 'book' | 'brain' | 'broadcast' | 'calculator' | 'cardholder';
	/**
	 *  Item Label
	 */
	label?: string;
	/**
	 * Flag for Normal or FastTrack
	 */
	type?: string;
}
export interface IVHSSignupProps extends IBaseElementProps {
	code?: string;
	/**
	 * Radios
	 */
	radios?: IVHSSignupRadios[];
	/**
	 * Toggle Label
	 */
	toggleLabel?: string;
	/**
	 * Tooltip Label
	 */
	tooltipLabel?: string;
	/**
	 * Switch Label
	 */
	switchLabel?: string;
	/**
	 * Button Label
	 */
	buttonLabel?: string;
	/**
	 * Footnote
	 * @control richtext
	 */
	footnote?: string;
	/**
	 * List
	 */
	items?: IVHSSignupItems[];
	/**
	 * Is Sticky
	 */
	sticky?: boolean;
	/**
	 * Has Hero
	 */
	hero?: boolean;
}

/**
 * Signup
 * @block
 * @icon columns
 */
const VHSSignup: React.FC<IVHSSignupProps> = withAppContext(
	({
	context,
	code,
	radios = [],
	toggleLabel,
	tooltipLabel,
	switchLabel,
	buttonLabel,
	footnote,
	items = [],
	sticky,
	hero,
}) => {
	const { Api } = useContext(context);
	const [showWrapper, setShowWrapper] = useState(false);
	const [loginForm, setLoginForm] = useState('');
	const [fastTrack, setFastTrack] = useState( false );
	const [regionType, setRegionType] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [price, setPrice] = useState('canada');

	const [busy, setBusy] = useState(false);
	const handleWrapper = () => {
		setShowWrapper(!showWrapper);
		if(!showWrapper) {
			document.body.style.top = `-${window.scrollY}px`;
			document.body.style.position = 'fixed';
			document.body.style.width = '100%';
		} else {
			document.documentElement.style.scrollBehavior = 'auto';
			const scrollY = document.body.style.top;
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			window.scrollTo({ top: (parseInt(scrollY || '0') * -1), behavior: 'auto' });
		}
	};

	const handleFastTrackSwitch = () => {
		setFastTrack( ! fastTrack );
	};

	const handlePurchaseCourse = () => {
		setBusy(true);
		Api.addCourseToCart([{ 'code' : code, 'fastTrack': fastTrack, 'regionType': regionType }])
			.then((results) => {
				// check if the user is logged in
				if ( results.loginForm ) {
					setLoginForm( results.loginForm );
					setBusy(false);
				} else {
					//redirect to cart page
					window.location.href = results.url;
				}
			})
			.catch((message) => {
				setErrorMessage(message);
				setBusy(false);
			});
	};

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const initTooltip = () => {
			const tooltipTriggerList = [].slice.call(
				ref.current.querySelectorAll('.vhs-signup__tooltip'),
			);

			tooltipTriggerList.map(function (tooltipTriggerEl) {
				return new Tooltip(tooltipTriggerEl);
			});
		};

		initTooltip();
	}, []);

	return (
		<div
			className={cn(
				'vhs-signup',
				sticky && 'vhs-signup--sticky',
				hero && 'vhs-signup--hero',
			)}
			ref={ref}
		>
			<div className={cn(
				'vhs-signup__container',
				showWrapper && 'vhs-signup__container--show',
			)}>
				<div className="vhs-signup__scroller">
					<div
					className={cn(
						'vhs-signup__nav',
						showWrapper && 'vhs-signup__nav--hide',
					)}
					>
						{radios.length > 0 &&
							<ul className="vhs-signup__prices">
								{radios.map(({ priceDollar, priceDollarFast, priceCent, priceCentFast,  priceIcon, name }, index) =>
									<li className="vhs-signup__price" key={index}>
										<div className="vhs-signup__radio-price">
											<div className="vhs-signup__radio-name">
												<i className={`icon icon-${priceIcon}`}></i>
											</div>
											${ ! fastTrack ? priceDollar : priceDollarFast }
											<span className="vhs-signup__radio-price-cent">.{ ! fastTrack ? priceCent : priceCentFast	}</span>
										</div>
									</li>
								)}
							</ul>
						}
						<Button
							className="vhs-signup__toggle btn btn-sm btn-primary"
							onClick={handleWrapper}
						>
							{toggleLabel}
							<Icon name="caret-up" />
						</Button>
					</div>
					<div className={cn(
							'vhs-signup__wrapper',
							showWrapper && 'vhs-signup__wrapper--show',
						)}
					>
						<Button
							className="vhs-signup__close"
							onClick={handleWrapper}
							aria-label="Close"
						>
							<Icon name="close" />
						</Button>
						<div className="vhs-signup__form">
							{radios.length > 0 &&
								<fieldset className="vhs-signup__radios">
									<legend>Prices</legend>
									{radios.map(({ priceDollar, priceCent, priceDollarFast, priceCentFast, priceIcon, name }, index) =>
										<div className={`vhs-signup__radio`} key={index}>
											<label htmlFor={name} className="vhs-signup__radio-label">
												<div className="vhs-signup__radio-price">
													${ ! fastTrack ? priceDollar : priceDollarFast }
													<span className="vhs-signup__radio-price-cent">.{ ! fastTrack ? priceCent : priceCentFast	}</span>
												</div>
												<div className="vhs-signup__radio-name">
													<i className={`icon icon-${priceIcon}`}></i>
													{name === 'canada' ? "canadian student price" : "international student price"}
												</div>
											</label>
										</div>
									)}
								</fieldset>
							}
							{switchLabel && (
								<label className="vhs-signup__switch">
									<input
										onChange={handleFastTrackSwitch}
										type="checkbox"
									/>
									<span
										aria-label="Toggle Fast Track"
										className="vhs-signup__switch-slider"
									></span>
									<span className="vhs-signup__switch-label">
										{switchLabel}
										<button
											type="button"
											className="vhs-signup__tooltip"
											data-bs-toggle="tooltip"
											data-bs-placement="top"
											data-bs-trigger="click"
											data-bs-title={tooltipLabel}
											aria-label="Tooltip"
										>
											<Icon name="info" />
										</button>
									</span>
								</label>
							)}
							{!loginForm && buttonLabel &&
								<>
								<button
										disabled={busy}
										onClick={handlePurchaseCourse}
										className="vhs-signup__button btn btn-primary"
								>
									{buttonLabel}
								</button>
								{busy && <Loading />}
								</>
							}
							{loginForm &&
								<RawHTML html={loginForm} />
							}
							{footnote && <div className="vhs-signup__footnote" dangerouslySetInnerHTML={{ __html:footnote }}/>}
							{errorMessage && (
								<div className="error-message">
									{errorMessage}
								</div>
							)}
						</div>
						{items.length > 0 &&
							<>
								{!fastTrack &&
								<ul className="vhs-signup__items vhs-signup__items_normal">
									{items.map(({label, icon, type}, index) => (
										// if type is normal or both, show the item
										(type === 'normal' || type === 'both') &&
										<li className="vhs-signup__item" key={index}>
											<>
												{icon && <i className={`icon icon-${icon}`}></i>}
												<span dangerouslySetInnerHTML={{__html: label}}/>
											</>
										</li>
									))}
								</ul>
								}
								{fastTrack &&
								<ul className="vhs-signup__items vhs-signup__items_fastTrack">
									{items.map(({label, icon, type}, index) => (
										// if type is normal or both, show the item
										(type === 'fastTrack' || type === 'both') &&
										<li className="vhs-signup__item" key={index}>
											<>
												{icon && <i className={`icon icon-${icon}`}></i>}
												<span dangerouslySetInnerHTML={{__html: label}}/>
											</>
										</li>
									))}
								</ul>
								}
							</>
						}
					</div>
				</div>
			</div>
		</div>
	);
},
);
export default VHSSignup;